import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import * as microsoftgraph from '@microsoft/microsoft-graph-types';
import {
  ReleaseDetailViewModel,
  ReleaseRoleViewModel,
  UserType,
} from 'src/app/shared/models/autogenerated-piping';

type ReleaseRoleGroup = 'owner' | 'reviewer' | 'contributor';

export interface ReleaseRolesSavePayload {
  releaseOwners: string[];
  internalReviewers: string[];
  contributors: string[];
}

@Component({
  selector: 'app-release-roles-tab',
  templateUrl: './release-roles-tab.component.html',
  styleUrls: ['./release-roles-tab.component.scss'],
  standalone: false,
})
export class ReleaseRolesTabComponent implements OnChanges {
  /** Full release detail payload loaded by the parent release detail page. */
  @Input() release: ReleaseDetailViewModel | null = null;

  /** True while the parent is persisting role changes. */
  @Input() isSaving: boolean = false;

  /** Emitted when the user requests to persist role changes. */
  @Output() saveRequested = new EventEmitter<ReleaseRolesSavePayload>();

  readonly faFloppyDisk = faFloppyDisk;

  releaseOwners: microsoftgraph.User[] = [];
  internalReviewers: microsoftgraph.User[] = [];
  contributors: microsoftgraph.User[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['release']) {
      this.loadRolesFromRelease();
    }
  }

  /** Display name for the immutable release author shown above editable role groups. */
  get creatorName(): string {
    return this.release?.author ?? 'N/A';
  }

  addMember(user: microsoftgraph.User, role: ReleaseRoleGroup): void {
    const list: microsoftgraph.User[] = this.getListForRole(role);
    const userId: string = user.id ?? '';
    if (!userId || list.some((item: microsoftgraph.User) => item.id === userId)) {
      return;
    }
    list.push(user);
  }

  removeMember(user: microsoftgraph.User, role: ReleaseRoleGroup): void {
    const userId: string = user.id ?? '';
    if (!userId) {
      return;
    }

    if (role === 'owner') {
      this.releaseOwners = this.releaseOwners.filter((item: microsoftgraph.User) => item.id !== userId);
      return;
    }

    if (role === 'reviewer') {
      this.internalReviewers = this.internalReviewers.filter((item: microsoftgraph.User) => item.id !== userId);
      return;
    }

    this.contributors = this.contributors.filter((item: microsoftgraph.User) => item.id !== userId);
  }

  /**
   * Builds a role payload from the current UI state.
   * The parent page will call the endpoint when it becomes available.
   */
  saveRoles(): void {
    if (this.isSaving) {
      return;
    }

    const payload: ReleaseRolesSavePayload = {
      releaseOwners: this.releaseOwners.map((user: microsoftgraph.User) => user.id ?? '').filter((id: string) => !!id),
      internalReviewers: this.internalReviewers.map((user: microsoftgraph.User) => user.id ?? '').filter((id: string) => !!id),
      contributors: this.contributors.map((user: microsoftgraph.User) => user.id ?? '').filter((id: string) => !!id),
    };
    this.saveRequested.emit(payload);
  }

  trackByUserId(index: number, user: microsoftgraph.User): string {
    return user.id ?? `${user.displayName ?? 'unknown'}-${index}`;
  }

  private getListForRole(role: ReleaseRoleGroup): microsoftgraph.User[] {
    if (role === 'owner') {
      return this.releaseOwners;
    }
    if (role === 'reviewer') {
      return this.internalReviewers;
    }
    return this.contributors;
  }

  private loadRolesFromRelease(): void {
    this.releaseOwners = [];
    this.internalReviewers = [];
    this.contributors = [];

    const roles = this.release?.roles ?? [];
    const dedupeMap: Map<string, true> = new Map<string, true>();

    roles.forEach((role: ReleaseRoleViewModel) => {
      const userId: string = role.userId ?? '';
      const userRole: UserType | undefined = role.userRole;
      if (!userId || userRole === undefined || userRole === UserType._0) {
        // Author is managed by the system and is intentionally excluded.
        return;
      }

      const dedupeKey: string = `${userId}-${userRole}`;
      if (dedupeMap.has(dedupeKey)) {
        return;
      }
      dedupeMap.set(dedupeKey, true);

      const user: microsoftgraph.User = {
        id: userId,
        displayName: role.userDisplayName ?? 'Unknown User',
      };

      if (userRole === UserType._1) {
        this.releaseOwners.push(user);
      } else if (userRole === UserType._2) {
        this.internalReviewers.push(user);
      } else if (userRole === UserType._3) {
        this.contributors.push(user);
      }
    });
  }
}